<template>
  <transition name="wab">
    <div v-if="waitingAlert" class="wab" role="alert">
      <div class="wab-head">
        <svg class="wab-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M5 22h14"/><path d="M5 2h14"/>
          <path d="M17 22v-4.17a2 2 0 0 0-.59-1.42L12 12l-4.41 4.41A2 2 0 0 0 7 17.83V22"/>
          <path d="M7 2v4.17a2 2 0 0 0 .59 1.42L12 12l4.41-4.41A2 2 0 0 0 17 6.17V2"/>
        </svg>
        <div class="wab-title">{{ alertTitle(waitingAlert) }}</div>
        <button type="button" class="wab-close" aria-label="Fechar" @click="dismissWaitingAlert">×</button>
      </div>
      <p class="wab-sub">Ninguém do time nem do fallback disponível pra receber.</p>
      <ul class="wab-list">
        <li v-for="c in shown" :key="c.conversationId">
          <span class="wab-name">{{ c.contactName }}</span>
          <span v-if="where(c)" class="wab-where">{{ where(c) }}</span>
          <span class="wab-min">{{ c.waitingMinutes }} min</span>
        </li>
      </ul>
      <div v-if="hidden > 0" class="wab-more">…e mais {{ hidden }}</div>
      <button type="button" class="wab-go" @click="goToQueue">Ver fila</button>
    </div>
  </transition>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import { waitingAlert, alertTitle, dismissWaitingAlert } from '@/composables/useWaitingAlert'
import type { WaitingAlertNotification } from '@/composables/useConversationRealtime'

const MAX_SHOWN = 5
const router = useRouter()

// Quem espera há mais tempo primeiro.
const sorted = computed(() => [...(waitingAlert.value?.conversations ?? [])].sort((a, b) => b.waitingMinutes - a.waitingMinutes))
const shown = computed(() => sorted.value.slice(0, MAX_SHOWN))
const hidden = computed(() => Math.max(0, (waitingAlert.value?.count ?? 0) - shown.value.length))

function where(c: WaitingAlertNotification['conversations'][number]) {
  return [c.teamName, c.flowName].filter(Boolean).join(' · ')
}

function goToQueue() {
  dismissWaitingAlert()
  router.push('/fila')
}
</script>

<style scoped>
.wab {
  position: fixed; right: 20px; bottom: 20px; z-index: 9000;
  width: min(360px, calc(100vw - 32px));
  background: var(--white); color: var(--ink-80);
  border: 1px solid var(--ink-10); border-left: 4px solid var(--orange);
  border-radius: 12px; box-shadow: 0 12px 32px rgba(0, 0, 0, .18);
  padding: 14px 16px;
}
.wab-head { display: flex; align-items: flex-start; gap: 8px; }
.wab-icon { width: 18px; height: 18px; flex-shrink: 0; color: var(--orange); margin-top: 1px; }
.wab-title { flex: 1; font-weight: 700; font-size: 13.5px; line-height: 1.35; }
.wab-close {
  border: none; background: none; color: var(--ink-40); font-size: 20px; line-height: 1;
  cursor: pointer; padding: 0 2px;
}
.wab-close:hover { color: var(--ink-80); }
.wab-sub { margin: 4px 0 8px 26px; font-size: 12px; color: var(--ink-60); }
.wab-list { list-style: none; margin: 0 0 0 26px; padding: 0; display: flex; flex-direction: column; gap: 4px; }
.wab-list li { display: flex; gap: 6px; align-items: baseline; font-size: 12.5px; min-width: 0; }
.wab-name { font-weight: 600; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.wab-where { color: var(--ink-40); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; flex: 1; min-width: 0; }
.wab-min { margin-left: auto; color: var(--orange); font-weight: 700; white-space: nowrap; }
.wab-more { margin: 4px 0 0 26px; font-size: 12px; color: var(--ink-40); }
.wab-go {
  margin: 10px 0 0 26px; padding: 6px 14px; border-radius: 999px; border: none;
  background: var(--red); color: #fff; font-weight: 700; font-size: 12px; cursor: pointer;
}
.wab-go:hover { filter: brightness(1.08); }

.wab-enter-active, .wab-leave-active { transition: opacity .2s, transform .2s; }
.wab-enter-from, .wab-leave-to { opacity: 0; transform: translateY(12px); }
</style>
